import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { memo, useCallback, useEffect, useState } from "react";
import HomeScreenPreview from "./Previews/HomeScreenPreview";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { fetchCustomizationData } from "../../../../Redux/actions/appCustomizationAction";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { customizationUpload } from "../../../../utils/upload";
import { useDropzone } from "react-dropzone";
import {
  setData,
  setEnabledSections,
} from "../../../../Redux/reducers/appCustomizationSlice";
import { PhotoIcon } from "../Icons";
import { uploadWrapper } from "../style";

const UploadImageItem: React.FC<any> = ({
  image,
  setFieldValue,
  readOnly,
  name,
  fieldName,
  studyId,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const onDrop = useCallback(
    async (acceptedFiles: any) => {
      try {
        setLoading(true);
        const file = acceptedFiles?.[0];
        if (file) {
          if (file.size > 10 * 1024 * 1024) {
            toastMessage("warning", "File Size cannot be greater than 10 MB!");
            return;
          }
          const url = await customizationUpload(file, studyId);
          if (url) {
            const obj = {
              previewUrl: url?.previewUrl,
              url: url?.postUploadImageUrl,
            };
            setFieldValue(fieldName, obj);
          }
        }
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [setFieldValue, fieldName, studyId]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: !readOnly ? onDrop : undefined,
    multiple: false,
    accept: {
      "image/jpg": [],
      "image/jpeg": [],
      "image/png": [],
    },
    noClick: readOnly,
  });

  return (
    <>
      {loading && (
        <Backdrop
          open={true}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Box {...getRootProps({ className: "dropzone" })} sx={uploadWrapper}>
        <input {...getInputProps()} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .preview-img": {
              width: "68px",
              height: "50px",
              borderRadius: "8px",
            },
          }}
        >
          {image ? (
            <img src={image} className="preview-img" alt={name} />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PhotoIcon />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

const HomeScreen = () => {
  const { id: studyId } = useParams();
  const dispatch = useAppDispatch();
  const selectedCategory = useAppSelector(
    (state) => state.appCustomization.category
  );
  const loading = useAppSelector((state) => state.appCustomization.loading);
  const homeScreenData = useAppSelector((state) => state.appCustomization.data);
  const toggleLoader = useAppSelector(
    (state) => state.appCustomization.toggleLoader
  );
  const enabledSections = useAppSelector(
    (state) => state.appCustomization.enabledSections
  );

  useEffect(() => {
    dispatch(fetchCustomizationData(studyId, selectedCategory));
    return () => {
      dispatch(setData([]));
      dispatch(setEnabledSections([]));
    };
  }, [dispatch, selectedCategory, studyId, toggleLoader]);

  const handleFieldChange = (key: string, fieldName: string, value: any) => {
    const updatedData = homeScreenData.map((item: any) =>
      item.key === key ? { ...item, [fieldName]: value } : item
    );
    const enabled = updatedData.filter((item: any) => item.enabled);
    dispatch(setData(updatedData));
    dispatch(setEnabledSections(enabled));
  };

  return (
    <Stack
      direction="row"
      justifyContent={"space-between"}
      sx={{ height: "100%" }}
    >
      {loading ? (
        <Box
          sx={{
            height: "100%",
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Stack
          sx={{
            flex: 1,
            minHeight: "1px",
            height: "100%",
            overflow: "auto",
            py: 2.5,
            px: 2,
            alignItems: "center",
            width: "100%",
          }}
          gap={2}
        >
          {homeScreenData.map((item: any) => (
            <Box
              key={item.key}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Stack>
                  <Checkbox
                    checked={item.enabled || false}
                    onClick={(e) => {
                      if (item.enabled && enabledSections.length < 3) {
                        e.preventDefault();
                      }
                    }}
                    onChange={() =>
                      handleFieldChange(item.key, "enabled", !item.enabled)
                    }
                  />
                </Stack>
                <Stack>
                  <Typography>Default</Typography>
                  <UploadImageItem
                    image={item?.defaultIcon?.previewUrl}
                    setFieldValue={(fieldName: string, value: any) =>
                      handleFieldChange(item.key, "defaultIcon", value)
                    }
                    readOnly={false}
                    fieldName={"defaultIcon"}
                    name={`Default Icon for ${item.text}`}
                    studyId={studyId}
                    dispatch={dispatch}
                    loading={loading}
                  />
                </Stack>

                <Stack>
                  <Typography>Active</Typography>
                  <UploadImageItem
                    image={item?.activeIcon?.previewUrl}
                    setFieldValue={(fieldName: string, value: any) =>
                      handleFieldChange(item.key, "activeIcon", value)
                    }
                    readOnly={false}
                    fieldName={"activeIcon"}
                    name={`Active Icon for ${item.text}`}
                    studyId={studyId}
                    dispatch={dispatch}
                    loading={loading}
                  />
                </Stack>

                <Stack sx={{ width: "100%" }}>
                  <Typography>Text</Typography>
                  <TextField
                    value={item.text}
                    onChange={(e) =>
                      handleFieldChange(item.key, "text", e.target.value)
                    }
                    inputProps={{ maxLength: 10 }}
                    variant="outlined"
                    placeholder="Enter text"
                  />
                </Stack>
                <Stack sx={{ width: "100%" }}>
                  <Typography>Connector</Typography>
                  <Select
                    value={item.connector || ""}
                    onChange={(e) =>
                      handleFieldChange(item.key, "connector", e.target.value)
                    }
                  >
                    <MenuItem value="Menu">Menu</MenuItem>
                    <MenuItem value="Home">Home</MenuItem>
                    <MenuItem value="Learn">Learn</MenuItem>
                    <MenuItem value="Scan">Scan</MenuItem>
                    <MenuItem value="Profile">Profile</MenuItem>
                  </Select>
                </Stack>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
      <Box sx={{ ml: "auto", height: "100%" }}>
        <HomeScreenPreview />
      </Box>
    </Stack>
  );
};

export default memo(HomeScreen);
